import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from "lucide-react";

/*
 * In-page messages, replacing the floating sonner toaster.
 *
 * Two reasons this exists:
 *
 * 1. Product: a toast in the corner of the viewport is easy to miss, cannot be
 *    re-read once it times out, and on a failed form submit it puts the reason
 *    for the failure nowhere near the field that caused it.
 *
 * 2. Design system: <Toaster richColors /> rendered sonner's own palette,
 *    rounded corners and soft box-shadow — none of which exist in this system.
 *    It was the single most-seen surface in the product (89 call sites) and it
 *    was on none of the design system.
 *
 * The API is deliberately sonner-shaped (`notify.error("…")`) so call sites
 * only change their import, and — like sonner — dispatch goes through a module
 * level emitter rather than a context, so a plain function outside React can
 * still raise a message.
 */

let _id = 0;
const listeners = new Set();
let items = [];

function emit(next) {
  items = next;
  listeners.forEach((fn) => fn(items));
}

function push(level, message, opts = {}) {
  const text = typeof message === "string" ? message : String(message ?? "");
  if (!text.trim()) return null;
  const id = ++_id;
  // Repeating an identical message should re-assert the existing one rather
  // than stack duplicates — a retry loop otherwise fills the page.
  const deduped = items.filter((n) => !(n.level === level && n.text === text));
  emit([...deduped, { id, level, text, title: opts.title }]);
  return id;
}

export function dismissNotice(id) {
  emit(items.filter((n) => n.id !== id));
}

export function clearNotices() {
  emit([]);
}

export const notify = {
  error: (m, o) => push("error", m, o),
  success: (m, o) => push("success", m, o),
  warning: (m, o) => push("warning", m, o),
  info: (m, o) => push("info", m, o),
  message: (m, o) => push("info", m, o),
  dismiss: dismissNotice,
  clear: clearNotices,
};

/** Drop-in for `import { toast } from "sonner"`. */
export const toast = notify;

const LEVELS = {
  error: {
    label: "ERROR",
    Icon: AlertCircle,
    // Tokens only — every pairing here is verified by scripts/check-contrast.py.
    surface: "bg-state-danger-surface",
    ink: "text-ink-accent",
    bar: "bg-brand",
    // Errors are assertive: the user just tried something and it failed.
    role: "alert",
    live: "assertive",
    sticky: true,
  },
  warning: {
    label: "WARNING",
    Icon: TriangleAlert,
    surface: "bg-state-warning-surface",
    ink: "text-state-warning-ink",
    bar: "bg-state-warning",
    role: "alert",
    live: "assertive",
    sticky: true,
  },
  success: {
    label: "DONE",
    Icon: CheckCircle2,
    surface: "bg-state-success-surface",
    ink: "text-state-success",
    bar: "bg-state-success",
    role: "status",
    live: "polite",
    sticky: false,
  },
  info: {
    label: "NOTE",
    Icon: Info,
    surface: "bg-surface",
    ink: "text-ink",
    bar: "bg-ink",
    role: "status",
    live: "polite",
    sticky: false,
  },
};

const AUTO_DISMISS_MS = 6000;

function Notice({ notice }) {
  const cfg = LEVELS[notice.level] || LEVELS.info;
  const { Icon } = cfg;

  useEffect(() => {
    if (cfg.sticky) return undefined;
    const t = setTimeout(() => dismissNotice(notice.id), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [notice.id, cfg.sticky]);

  return (
    <div
      role={cfg.role}
      aria-live={cfg.live}
      className={`brutal-border ${cfg.surface} flex items-stretch`}
      data-testid={`notice-${notice.level}`}
    >
      {/* Solid state bar: the level is carried by position and label as well
          as hue, so it does not depend on colour alone. */}
      <div className={`w-1.5 shrink-0 ${cfg.bar}`} aria-hidden="true" />
      <div className="flex items-start gap-3 px-4 py-3 flex-1 min-w-0">
        <Icon size={16} className={`${cfg.ink} shrink-0 mt-0.5`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className={`text-2xs uppercase tracking-widest font-semibold ${cfg.ink}`}>
            {notice.title || cfg.label}
          </div>
          <p className="font-mono-ui text-sm mt-0.5 break-words">{notice.text}</p>
        </div>
        <button
          type="button"
          onClick={() => dismissNotice(notice.id)}
          aria-label="Dismiss message"
          className="shrink-0 p-1 -m-1 hover:bg-surface-sunken"
          data-testid={`notice-dismiss-${notice.id}`}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/**
 * Renders wherever it is placed — put it at the top of a page's <main>, in the
 * normal document flow. It is intentionally not fixed-position: the whole point
 * is that the message occupies layout space and stays until dismissed.
 */
export function NoticeRegion({ className = "" }) {
  const [list, setList] = useState(items);

  useEffect(() => {
    listeners.add(setList);
    return () => listeners.delete(setList);
  }, []);

  // Messages are per-view; a stale error from the previous page is confusing.
  useEffect(() => () => clearNotices(), []);

  if (!list.length) return null;
  return (
    <div className={`space-y-2 mb-6 ${className}`} data-testid="notice-region">
      {list.map((n) => <Notice key={n.id} notice={n} />)}
    </div>
  );
}
